import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { SectionRow, DesignPanel } from "@/pages/tools/htmlStudioEditors";
import {
  Code2, Sparkles, Loader2, Monitor, Tablet, Smartphone, Eye, Code, Copy, Download,
  Save, FolderOpen, ArrowLeft, Mail, Layout, Lock, Wand2, FilePlus2, Plus, X, Send,
} from "lucide-react";

const BRIEF_FIELDS = [
  { key: "goal", label: "Goal / objective", placeholder: "e.g. Fill the April Open Water course" },
  { key: "offer", label: "Offer / product / course", placeholder: "e.g. Open Water course, 4 spots left, €399" },
  { key: "cta", label: "Primary call to action", placeholder: "e.g. Reserve your spot" },
  { key: "url", label: "Destination URL", placeholder: "https://" },
  { key: "audience", label: "Who is this for? (optional)", placeholder: "Uses your Brand Profile audience if blank" },
  { key: "deadline", label: "Deadline / urgency (optional)", placeholder: "e.g. Offer ends Sunday" },
  { key: "details", label: "Anything important to include? (optional)", placeholder: "Key points, proof, dates…", area: true },
];

export default function HtmlStudio() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sub } = useSubscription();

  const [config, setConfig] = useState(null);
  const [stage, setStage] = useState("choose"); // choose | brief | editor
  const [assetType, setAssetType] = useState("");
  const [template, setTemplate] = useState("brand");
  const [startMode, setStartMode] = useState("ai"); // ai | blank
  const [sourceVaultId, setSourceVaultId] = useState(null);
  const [brief, setBrief] = useState({});

  const [project, setProject] = useState(null);
  const [design, setDesign] = useState(null);
  const [html, setHtml] = useState("");
  const [warnings, setWarnings] = useState([]);

  const [generating, setGenerating] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("visual"); // visual | code
  const [device, setDevice] = useState("desktop");
  const [openSection, setOpenSection] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [projectTitle, setProjectTitle] = useState("");
  const [projects, setProjects] = useState([]);
  const [showProjects, setShowProjects] = useState(false);
  const [showSendTest, setShowSendTest] = useState(false);
  const [testEmail, setTestEmail] = useState(() => localStorage.getItem("dms_test_email") || "");
  const [sendingTest, setSendingTest] = useState(false);

  const mode = project?.mode || (config?.asset_types?.find((a) => a.id === assetType)?.mode) || "email";

  // ---- Load config ----
  useEffect(() => {
    api.get("/html-studio/config")
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({ has_access: false, asset_types: [], templates: [], email_fonts: [], web_fonts: [] }));
  }, [sub?.tier]);

  // ---- Hand-off import (?from=<vaultId>) + open saved (?project=<id>) ----
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const proj = q.get("project");
    if (proj && config?.has_access) {
      openProject(proj);
      return;
    }
    const from = q.get("from");
    if (!from || !config?.has_access) return;
    api.get(`/html-studio/import/${from}`)
      .then(({ data }) => {
        setSourceVaultId(from);
        setAssetType(data.suggested_asset_type);
        setStartMode("ai");
        setBrief((b) => ({ ...b, details: `Adapt this existing "${data.title || "content"}" into the asset.` }));
        setStage("brief");
        toast.info("Loaded your content — pick a type and generate the branded HTML.");
      })
      .catch(() => { toast.error("Couldn't load that content — starting fresh."); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, config?.has_access]);

  // ---- Render (debounced) ----
  const renderNow = useCallback(async (proj, des) => {
    if (!proj || !des) return;
    setRendering(true);
    try {
      const { data } = await api.post("/html-studio/render", { project: proj, design: des });
      setHtml(data.html);
      setWarnings(data.warnings || []);
    } catch (e) { console.warn("HTML render failed, keeping last good output", e); } finally { setRendering(false); }
  }, []);

  useEffect(() => {
    if (stage !== "editor" || !project || !design) return;
    const t = setTimeout(() => renderNow(project, design), 450);
    return () => clearTimeout(t);
  }, [project, design, stage, renderNow]);

  const loadProjects = () => api.get("/html-studio/projects").then(({ data }) => setProjects(data.items || [])).catch(() => {});

  // ---- Actions ----
  const generate = async () => {
    if (!assetType) { toast.error("Pick what you're building first."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/html-studio/generate", {
        asset_type: assetType, brief, template, source_vault_id: sourceVaultId, mode_of_start: startMode,
      });
      setProject(data.project);
      setDesign(data.design);
      setProjectTitle(data.project.project_name || "Untitled");
      setProjectId(null);
      setStage("editor");
      setView("visual");
      renderNow(data.project, data.design);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402) toast.error(typeof d === "object" ? d.message : "Pro Unlimited required.");
      else toast.error(typeof d === "string" ? d : "Could not generate. Try again.");
    } finally { setGenerating(false); }
  };

  const saveProject = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/html-studio/projects", {
        id: projectId, title: projectTitle, project, design,
      });
      setProjectId(data.id);
      toast.success("Saved to your Content Vault");
    } catch { toast.error("Could not save."); } finally { setSaving(false); }
  };

  const openProject = async (id) => {
    try {
      const { data } = await api.get(`/html-studio/projects/${id}`);
      setProject(data.project);
      setDesign(data.design);
      setProjectId(id);
      setProjectTitle(data.title || "Untitled");
      setAssetType(data.project?.asset_type || "");
      setStage("editor");
      setShowProjects(false);
      renderNow(data.project, data.design);
    } catch { toast.error("Could not open project."); }
  };

  const deleteProject = async (id) => {
    try { await api.delete(`/html-studio/projects/${id}`); loadProjects(); toast.success("Deleted"); }
    catch { toast.error("Could not delete."); }
  };

  const resetBrandColors = () => {
    const dd = mode === "email" ? config?.default_design_email : config?.default_design_web;
    if (dd) setDesign((d) => ({ ...d, primary: dd.primary, secondary: dd.secondary, accent: dd.accent, background: dd.background, heading_color: dd.heading_color, text_color: dd.text_color, button_bg: dd.button_bg, button_text_color: dd.button_text_color, link_color: dd.link_color }));
  };

  const saveToBrand = async () => {
    try {
      await api.put("/brand-kit", {
        primary_color: design.primary, secondary_color: design.secondary, accent_color: design.accent,
      });
      toast.success("Brand Profile updated");
    } catch { toast.error("Could not update Brand Profile (owner only)."); }
  };

  const copyHtml = () => { navigator.clipboard.writeText(html); toast.success("HTML copied"); };
  const downloadHtml = () => {
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${(projectTitle || "dms-asset").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.html`;
    a.click(); URL.revokeObjectURL(url);
  };

  const sendTest = async () => {
    const to = testEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) { toast.error("Enter a valid email address."); return; }
    setSendingTest(true);
    try {
      localStorage.setItem("dms_test_email", to);
      const { data } = await api.post("/html-studio/send-test", { to_email: to, project, design, subject: project?.subject });
      toast[data.stubbed ? "info" : "success"](data.message || "Test email sent.");
      setShowSendTest(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send the test email.");
    } finally { setSendingTest(false); }
  };
  // ---- Section mutations ----
  const updateSection = (idx, next) => setProject((p) => ({ ...p, sections: p.sections.map((s, i) => (i === idx ? next : s)) }));
  const moveSection = (idx, dir) => setProject((p) => {
    const arr = [...p.sections]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...p, sections: arr };
  });
  const deleteSection = (idx) => setProject((p) => ({ ...p, sections: p.sections.filter((_, i) => i !== idx) }));
  const dupSection = (idx) => setProject((p) => {
    const arr = [...p.sections];
    arr.splice(idx + 1, 0, { ...arr[idx], id: Math.random().toString(36).slice(2, 12) });
    return { ...p, sections: arr };
  });
  const addSection = (type) => setProject((p) => ({
    ...p,
    sections: [...p.sections.slice(0, p.sections.length - 1),
      { id: Math.random().toString(36).slice(2, 12), type },
      p.sections[p.sections.length - 1]],
  }));

  const deviceWidth = useMemo(() => {
    if (mode === "email") return device === "mobile" ? 380 : 660;
    return device === "mobile" ? 390 : device === "tablet" ? 820 : "100%";
  }, [device, mode]);

  // ---- Access gate ----
  if (config && !config.has_access) {
    return (
      <div className="px-5 sm:px-6 lg:px-10 py-10 max-w-3xl" data-testid="html-studio-locked">
        <div className="dms-label mb-3">Branded HTML Studio</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">Design branded emails & pages</h1>
        <div className="mt-6 dms-card p-6 sm:p-8 border-[#4FD1C5]/40 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#4FD1C5]/10" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase font-bold text-[#0A2342]"><Lock className="w-3.5 h-3.5" /> Pro Unlimited</div>
            <h2 className="mt-2 font-heading font-bold text-xl text-[#0A2342]">Turn any content into production-ready HTML</h2>
            <p className="mt-2 text-[14px] text-[#475569] max-w-xl">
              Generate on-brand marketing emails, landing pages and sales pages using your Brand Profile — no code required. Live preview, colour & font controls, and one-click export. Unlimited, zero credits.
            </p>
            <Link to="/billing" className="mt-5 inline-flex dms-btn-accent" data-testid="html-studio-upgrade">Upgrade to Pro Unlimited</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div className="p-10 text-[#64748B] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading Studio…</div>;
  }

  // ---- CHOOSE stage ----
  if (stage === "choose") {
    const grouped = { Email: [], Web: [] };
    config.asset_types.forEach((a) => grouped[a.group]?.push(a));
    return (
      <div className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-5xl" data-testid="html-studio-choose">
        <div className="flex items-center justify-between">
          <div>
            <div className="dms-label mb-2">Branded HTML Studio</div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">What are you building?</h1>
            <p className="mt-2 text-[#475569] text-[14.5px] max-w-2xl">Every asset uses your Brand Profile automatically. Premium · Unlimited · Zero credits.</p>
          </div>
          <button onClick={() => { loadProjects(); setShowProjects(true); }} data-testid="open-projects" className="dms-btn-ghost text-[13px] whitespace-nowrap"><FolderOpen className="w-4 h-4" /> My projects</button>
        </div>

        {["Email", "Web"].map((g) => (
          <div key={g} className="mt-8">
            <div className="flex items-center gap-2 mb-3 text-[#0A2342] font-semibold">
              {g === "Email" ? <Mail className="w-4 h-4" /> : <Layout className="w-4 h-4" />} {g}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {grouped[g].map((a) => (
                <button
                  key={a.id}
                  data-testid={`asset-${a.id}`}
                  onClick={() => { setAssetType(a.id); setStage("brief"); }}
                  className={`text-left dms-card dms-card-hover p-4 ${assetType === a.id ? "ring-2 ring-[#4FD1C5]" : ""}`}
                >
                  <div className="font-heading font-bold text-[14px] text-[#0A2342] leading-snug">{a.label}</div>
                  <div className="text-[11.5px] text-[#94A3B8] mt-1 capitalize">{a.mode} HTML</div>
                </button>
              ))}
            </div>
          </div>
        ))}

        {showProjects && <ProjectsDrawer projects={projects} onClose={() => setShowProjects(false)} onOpen={openProject} onDelete={deleteProject} />}
      </div>
    );
  }

  // ---- BRIEF stage ----
  if (stage === "brief") {
    const at = config.asset_types.find((a) => a.id === assetType);
    return (
      <div className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-2xl" data-testid="html-studio-brief">
        <button onClick={() => setStage("choose")} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#64748B] hover:text-[#0A2342] mb-4"><ArrowLeft className="w-4 h-4" /> Change type</button>
        <div className="dms-label mb-2">{at?.label} · {at?.mode} HTML</div>
        <h1 className="text-2xl font-heading font-bold text-[#0A2342]">How would you like to start?</h1>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button onClick={() => setStartMode("ai")} data-testid="start-ai" className={`text-left dms-card p-4 ${startMode === "ai" ? "ring-2 ring-[#4FD1C5]" : "dms-card-hover"}`}>
            <Wand2 className="w-5 h-5 text-[#1D4E89]" />
            <div className="mt-2 font-heading font-bold text-[14px] text-[#0A2342]">Generate with AI</div>
            <div className="text-[12px] text-[#64748B] mt-1">Brand-aware copy + structure, ready to edit.</div>
          </button>
          <button onClick={() => setStartMode("blank")} data-testid="start-blank" className={`text-left dms-card p-4 ${startMode === "blank" ? "ring-2 ring-[#4FD1C5]" : "dms-card-hover"}`}>
            <FilePlus2 className="w-5 h-5 text-[#1D4E89]" />
            <div className="mt-2 font-heading font-bold text-[14px] text-[#0A2342]">Start blank</div>
            <div className="text-[12px] text-[#64748B] mt-1">Placeholder sections you edit yourself.</div>
          </button>
        </div>

        <div className="mt-5">
          <label className="dms-label block mb-2">Template</label>
          <div className="flex flex-wrap gap-2">
            {config.templates.filter((t) => t.modes.includes(at?.mode)).map((t) => (
              <button key={t.id} onClick={() => setTemplate(t.id)} data-testid={`template-${t.id}`}
                className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold border ${template === t.id ? "border-[#4FD1C5] bg-[#4FD1C5]/10 text-[#0A2342]" : "border-[#E2E8F0] text-[#64748B]"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {startMode === "ai" && (
          <div className="mt-6 space-y-4">
            {BRIEF_FIELDS.map((f) => (
              <label key={f.key} className="block">
                <span className="dms-label block mb-1.5">{f.label}</span>
                {f.area ? (
                  <textarea data-testid={`brief-${f.key}`} rows={3} value={brief[f.key] || ""} placeholder={f.placeholder}
                    onChange={(e) => setBrief((b) => ({ ...b, [f.key]: e.target.value }))} className="dms-textarea" />
                ) : (
                  <input data-testid={`brief-${f.key}`} value={brief[f.key] || ""} placeholder={f.placeholder}
                    onChange={(e) => setBrief((b) => ({ ...b, [f.key]: e.target.value }))} className="dms-input" />
                )}
              </label>
            ))}
          </div>
        )}

        <button onClick={generate} disabled={generating} data-testid="studio-generate" className="mt-6 dms-btn-primary">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Building…</> : <><Sparkles className="w-4 h-4" /> {startMode === "ai" ? "Generate branded HTML" : "Create blank project"}</>}
        </button>
      </div>
    );
  }

  // ---- EDITOR stage (3-panel) ----
  return (
    <div className="h-[calc(100vh-56px)] flex flex-col" data-testid="html-studio-editor">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[#E2E8F0] bg-white">
        <button onClick={() => setStage("choose")} className="text-[#64748B] hover:text-[#0A2342]" data-testid="editor-back"><ArrowLeft className="w-4 h-4" /></button>
        <Code2 className="w-4 h-4 text-[#1D4E89]" />
        <input value={projectTitle} onChange={(e) => setProjectTitle(e.target.value)} data-testid="project-title"
          className="flex-1 min-w-0 text-[14px] font-semibold text-[#0A2342] bg-transparent focus:outline-none" />
        {rendering && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#94A3B8]" />}
        <button onClick={() => { loadProjects(); setShowProjects(true); }} className="dms-btn-ghost text-[12.5px] py-1.5 px-2.5 hidden sm:inline-flex" data-testid="editor-projects"><FolderOpen className="w-3.5 h-3.5" /> Projects</button>
        <button onClick={copyHtml} className="dms-btn-ghost text-[12.5px] py-1.5 px-2.5" data-testid="editor-copy"><Copy className="w-3.5 h-3.5" /> Copy</button>
        <button onClick={downloadHtml} className="dms-btn-ghost text-[12.5px] py-1.5 px-2.5" data-testid="editor-download"><Download className="w-3.5 h-3.5" /> Download</button>
        {mode === "email" && (
          <button onClick={() => setShowSendTest(true)} className="dms-btn-ghost text-[12.5px] py-1.5 px-2.5" data-testid="editor-send-test"><Send className="w-3.5 h-3.5" /> Send test</button>
        )}
        <button onClick={saveProject} disabled={saving} className="dms-btn-primary text-[12.5px] py-1.5 px-3" data-testid="editor-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* LEFT — content / sections */}
        <div className="w-72 shrink-0 border-r border-[#E2E8F0] bg-[#F8FAFC] overflow-y-auto p-3 hidden lg:block" data-testid="panel-content">
          {mode === "email" && (
            <div className="space-y-2 mb-4">
              <label className="block"><span className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#64748B]">Subject line</span>
                <input value={project.subject || ""} onChange={(e) => setProject((p) => ({ ...p, subject: e.target.value }))} data-testid="email-subject" className="mt-1 w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-[13px]" /></label>
              <label className="block"><span className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#64748B]">Preheader</span>
                <input value={project.preheader || ""} onChange={(e) => setProject((p) => ({ ...p, preheader: e.target.value }))} data-testid="email-preheader" className="mt-1 w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-[13px]" /></label>
            </div>
          )}
          <div className="dms-label mb-2">Sections</div>
          <div className="space-y-2">
            {project.sections.map((s, i) => (
              <SectionRow key={s.id || i} section={s} index={i} total={project.sections.length}
                open={openSection === (s.id || i)}
                onToggle={() => setOpenSection(openSection === (s.id || i) ? null : (s.id || i))}
                onMove={(dir) => moveSection(i, dir)} onDelete={() => deleteSection(i)}
                onDuplicate={() => dupSection(i)} onChange={(next) => updateSection(i, next)} />
            ))}
          </div>
          <AddSectionMenu mode={mode} onAdd={addSection} />
        </div>

        {/* CENTER — live preview / code */}
        <div className="flex-1 min-w-0 flex flex-col bg-[#EEF2F6]">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#E2E8F0] bg-white">
            <div className="flex gap-1">
              <button onClick={() => setView("visual")} data-testid="view-visual" className={`px-2.5 py-1.5 rounded-md text-[12.5px] font-semibold inline-flex items-center gap-1.5 ${view === "visual" ? "bg-[#0A2342] text-white" : "text-[#64748B]"}`}><Eye className="w-3.5 h-3.5" /> Visual</button>
              <button onClick={() => setView("code")} data-testid="view-code" className={`px-2.5 py-1.5 rounded-md text-[12.5px] font-semibold inline-flex items-center gap-1.5 ${view === "code" ? "bg-[#0A2342] text-white" : "text-[#64748B]"}`}><Code className="w-3.5 h-3.5" /> Code</button>
            </div>
            {view === "visual" && (
              <div className="flex gap-1">
                <button onClick={() => setDevice("desktop")} data-testid="device-desktop" className={`p-1.5 rounded ${device === "desktop" ? "bg-[#4FD1C5]/20 text-[#0A2342]" : "text-[#94A3B8]"}`}><Monitor className="w-4 h-4" /></button>
                {mode === "web" && <button onClick={() => setDevice("tablet")} data-testid="device-tablet" className={`p-1.5 rounded ${device === "tablet" ? "bg-[#4FD1C5]/20 text-[#0A2342]" : "text-[#94A3B8]"}`}><Tablet className="w-4 h-4" /></button>}
                <button onClick={() => setDevice("mobile")} data-testid="device-mobile" className={`p-1.5 rounded ${device === "mobile" ? "bg-[#4FD1C5]/20 text-[#0A2342]" : "text-[#94A3B8]"}`}><Smartphone className="w-4 h-4" /></button>
              </div>
            )}
          </div>

          {warnings.length > 0 && view === "visual" && (
            <div className="px-4 py-2 bg-[#FEFCE8] border-b border-[#FCD34D] text-[12px] text-[#92400E]" data-testid="email-warnings">
              {warnings[0]}
            </div>
          )}

          <div className="flex-1 overflow-auto p-4 flex justify-center">
            {view === "visual" ? (
              <iframe
                title="preview"
                data-testid="studio-preview"
                srcDoc={html}
                sandbox="allow-same-origin"
                style={{ width: deviceWidth, maxWidth: "100%", height: "100%", minHeight: 600, border: "none", background: "#fff", borderRadius: 8, boxShadow: "0 10px 40px -20px rgba(0,0,0,0.35)" }}
              />
            ) : (
              <pre data-testid="studio-code" className="w-full text-[11.5px] leading-relaxed text-[#E2E8F0] bg-[#0F172A] rounded-lg p-4 overflow-auto whitespace-pre-wrap break-words">{html}</pre>
            )}
          </div>
        </div>

        {/* RIGHT — design */}
        <div className="w-72 shrink-0 border-l border-[#E2E8F0] bg-white overflow-y-auto p-4 hidden xl:block" data-testid="panel-design">
          {design && (
            <DesignPanel design={design} onChange={setDesign} mode={mode}
              emailFonts={config.email_fonts} webFonts={config.web_fonts}
              onResetBrand={resetBrandColors} onSaveToBrand={saveToBrand} />
          )}
        </div>
      </div>

      {showProjects && <ProjectsDrawer projects={projects} onClose={() => setShowProjects(false)} onOpen={openProject} onDelete={deleteProject} />}

      {showSendTest && (
        <div className="fixed inset-0 z-50 bg-[#0A2342]/40 flex items-center justify-center p-4" data-testid="send-test-modal" onClick={() => setShowSendTest(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-[#0A2342] text-lg flex items-center gap-2"><Send className="w-4 h-4 text-[#1D4E89]" /> Send test email</h3>
              <button onClick={() => setShowSendTest(false)} data-testid="send-test-close" className="text-[#94A3B8] hover:text-[#0A2342]"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[13px] text-[#64748B] mt-2 leading-relaxed">We'll send this exact email to your inbox so you can check how it renders before exporting. Send yourself a copy and preview it in a real client (Gmail, Outlook, Apple Mail).</p>
            <label className="dms-label mt-4 block">Send to</label>
            <input type="email" value={testEmail} data-testid="send-test-email" onChange={(e) => setTestEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") sendTest(); }}
              placeholder="you@example.com"
              className="mt-1 w-full px-3 py-2.5 rounded-md border border-[#E2E8F0] text-[14px] text-[#0A2342] focus:outline-none focus:border-[#4FD1C5]" />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowSendTest(false)} className="dms-btn-ghost text-[13px]">Cancel</button>
              <button onClick={sendTest} disabled={sendingTest} data-testid="send-test-submit" className="dms-btn-primary text-[13px]">
                {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddSectionMenu({ mode, onAdd }) {
  const [open, setOpen] = useState(false);
  const types = mode === "email"
    ? ["hero", "text", "features", "image", "quote", "cta"]
    : ["hero", "text", "features", "image", "quote", "pricing", "faq", "cta"];
  return (
    <div className="mt-3">
      <button onClick={() => setOpen(!open)} data-testid="add-section" className="w-full inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-[#1D4E89] hover:text-[#0A2342] border border-dashed border-[#CBD5E1] rounded-md py-2">
        <Plus className="w-3.5 h-3.5" /> Add section
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          {types.map((t) => (
            <button key={t} onClick={() => { onAdd(t); setOpen(false); }} data-testid={`add-section-${t}`}
              className="text-[12px] capitalize py-1.5 rounded-md border border-[#E2E8F0] hover:border-[#4FD1C5] text-[#0A2342]">{t}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectsDrawer({ projects, onClose, onOpen, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="projects-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white h-full overflow-y-auto p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-[#0A2342]">My HTML projects</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#64748B]" /></button>
        </div>
        {projects.length === 0 ? (
          <div className="text-[13.5px] text-[#94A3B8]">No saved projects yet.</div>
        ) : projects.map((p) => (
          <div key={p.id} className="dms-card p-3 mb-2 flex items-center gap-2" data-testid={`project-${p.id}`}>
            <button onClick={() => onOpen(p.id)} className="flex-1 text-left min-w-0">
              <div className="font-semibold text-[13.5px] text-[#0A2342] truncate">{p.title}</div>
              <div className="text-[11.5px] text-[#94A3B8] capitalize">{p.mode} · {p.asset_type?.replace(/_/g, " ")}</div>
            </button>
            <button onClick={() => onDelete(p.id)} className="text-[#B91C1C] hover:text-[#7F1D1D]"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
